import { Button, Checkbox } from 'antd';
import React, { useEffect, useState } from 'react';
import { batch } from 'react-redux';
import {
	AdvanceReportingSetupInfoStore,
	AdvancedReportingMessageFilterOptions,
	ControllerAdvancedReportingSetup,
	DisableReportingOfEvent,
	MessageFiltersType,
} from '../../../../../../../model/DeviceAdminModel';
import { useStoreDispatch, useStoreSelector } from '../../../../../../../store';
import {
	setControllerHasChanged,
	setControllerReportingAdvanceSetupAction,
	setControllerReportingSetupAction,
} from '../../../../../../../store/deviceControl/controller/actions';
import { selectAdvanceReportingSetupInfo, selectAdvancedReportingDisableReportingOfInfo } from '../../../../../../../store/deviceControl/controller/selectors';
import { Divider, Modal, Select } from '../../../../../../common';
import styles from '../reporting.module.scss';

const defaultMessageFilter: AdvancedReportingMessageFilterOptions = {
	MessageFiltersType: MessageFiltersType.HostMessages,
	CmdCompletes: false,
	CmdResponses: false,
	Events: false,
	MidnightReport: false,
	MiscMessages: false,
	QueryResponses: false,
	TitleMessages: false,
	Transactions: false,
};
const defaultMessageDiagnosticOptions: DiagnosticOption = {
	EnableACBReporting: false,
	EnableCommandEcho: false,
	EnableMCZReporting: false,
	EnablePrintingSNET: false,
};

type DiagnosticOption = Pick<ControllerAdvancedReportingSetup, 'EnableCommandEcho' | 'EnableACBReporting' | 'EnableMCZReporting' | 'EnablePrintingSNET'>;
type Props = {
	handleOnHideModal: () => void;
};

const AdvanceReportingSetup: React.FC<Props> = ({ handleOnHideModal }) => {
	//#region state management variables
	const dispatch = useStoreDispatch();
	const [currentEventSelected, setCurrentEventSelected] = useState(MessageFiltersType.HostMessages);
	const [disableReportingOf, setDisableReportingOf] = useState<DisableReportingOfEvent>({ internalEvent: false, transactionsEvents: false });
	const [messagesDiagnosticOptions, setMessagesDiagnosticOptions] = useState<DiagnosticOption>(defaultMessageDiagnosticOptions);
	const [messageFilters, setMessageFilters] = useState<AdvancedReportingMessageFilterOptions[]>([defaultMessageFilter]);
	const advanceReportingSetupInfo: AdvanceReportingSetupInfoStore = useStoreSelector<AdvanceReportingSetupInfoStore>(selectAdvanceReportingSetupInfo);
	const disableReportingOfStore: DisableReportingOfEvent = useStoreSelector<DisableReportingOfEvent>(selectAdvancedReportingDisableReportingOfInfo);
	//#endregion

	useEffect(() => {
		batch(() => {
			setMessageFilters(advanceReportingSetupInfo.messagesFilters);
			setCurrentEventSelected(advanceReportingSetupInfo.messagesFilters[0].MessageFiltersType);
			setMessagesDiagnosticOptions({
				EnableCommandEcho: advanceReportingSetupInfo.enableCommandEcho,
				EnablePrintingSNET: advanceReportingSetupInfo.enablePrintingSNETMessagesOnLocalPrinter,
				EnableMCZReporting: advanceReportingSetupInfo.enableMCZReporting,
				EnableACBReporting: advanceReportingSetupInfo.enableACBReporting,
			});
			setDisableReportingOf({
				internalEvent: disableReportingOfStore.internalEvent,
				transactionsEvents: disableReportingOfStore.transactionsEvents,
			});
		});
	}, []);

	const handleChangeMessageType = (key: keyof AdvancedReportingMessageFilterOptions, checked: boolean) => {
		const findIndex = messageFilters.findIndex(x => x.MessageFiltersType === currentEventSelected);
		if (~findIndex) {
			const cloneState = [...messageFilters];
			const value = cloneState[findIndex];
			cloneState.splice(findIndex, 1, { ...value, [key]: checked });
			setMessageFilters(cloneState);
		}
	};

	const handleChangeDiagnosticOption = (key: Partial<typeof messagesDiagnosticOptions>) => {
		setMessagesDiagnosticOptions(prevState => ({
			...prevState,
			...key,
		}));
	};

	const handleChangeControllerReportingSetup = (key: Partial<typeof disableReportingOfStore>) => {
		setDisableReportingOf(prevState => ({
			...prevState,
			...key,
		}));
	};

	const handleOnApplyChanges = () => {
		batch(() => {
			dispatch(setControllerHasChanged(true));
			dispatch(setControllerReportingAdvanceSetupAction({ ...messagesDiagnosticOptions }));
			dispatch(
				setControllerReportingSetupAction({
					DisableReportingInternalEvents: disableReportingOf.internalEvent,
					DisableReportingTransactionEvents: disableReportingOf.transactionsEvents,
				})
			);
			messageFilters.forEach(x => {
				dispatch(
					setControllerReportingAdvanceSetupAction({
						MessageFilters: [...messageFilters],
					})
				);
			});
			handleOnHideModal();
		});
	};

	const currentMessageTypeToRender: AdvancedReportingMessageFilterOptions = messageFilters.find(x => x.MessageFiltersType === currentEventSelected);

	return (
		<Modal
			onCancel={handleOnHideModal}
			onClickOk={() => null}
			title={_('AdvancedReportingSetups')}
			visible={true}
			customZoomClass={styles.customZoomClass}
			width={800}
			footer={[
				<Button id='okButton' key='ok' type='primary' onClick={handleOnApplyChanges} className={styles.footerButton}>
					{_('Ok')}
				</Button>,
				<Button id='cancelButton' key='cancel' onClick={handleOnHideModal} className={styles.footerButton}>
					{_('Cancel')}
				</Button>,
			]}>
			<div className={styles.advancedReportingContainer}>
				<p className={styles.advanceReportingWarning}>{_('AdvancedReportingWarning')}</p>
				<div className={styles.container}>
					<div className={styles.messageFilters}>
						<span className={styles.advanceReportingSection}>{`${_('MessageFilters')}:`}</span>
						<Select
							className={styles.margin}
							id='controllerReportingMessageFilterDropdown'
							notUseCurrentParse
							value={currentEventSelected}
							options={advanceReportingSetupInfo.messagesTypesOptions}
							onChange={value => setCurrentEventSelected(messageFilters.find(x => x.MessageFiltersType === Number(value)).MessageFiltersType)}
							getPopupContainer={element => element}
							showSearch
						/>
						<div className={styles.checkboxes}>
							<div className={styles.checkboxesContent}>
								<div>
									<Checkbox
										id='controllerReportingMessageEventsCheckbox'
										onChange={e => handleChangeMessageType('Events', e.target.checked)}
										checked={currentMessageTypeToRender.Events}>
										{_('Events')}
									</Checkbox>
								</div>
								<div>
									<Checkbox
										id='controllerReportingMessageTransactionsCheckbox'
										onChange={e => handleChangeMessageType('Transactions', e.target.checked)}
										checked={currentMessageTypeToRender.Transactions}>
										{_('Transactions')}
									</Checkbox>
								</div>
								<div>
									<Checkbox
										id='controllerReportingMessageMidnightCheckbox'
										onChange={e => handleChangeMessageType('MidnightReport', e.target.checked)}
										checked={currentMessageTypeToRender.MidnightReport}>
										{_('MidnightReport')}
									</Checkbox>
								</div>
								<div>
									<Checkbox
										id='controllerReportingMessageCmdResponsesCheckbox'
										onChange={e => handleChangeMessageType('CmdResponses', e.target.checked)}
										checked={currentMessageTypeToRender.CmdResponses}>
										{_('CmdResponses')}
									</Checkbox>
								</div>
								<div>
									<Checkbox
										id='controllerReportingMessageCmdCompletesCheckbox'
										onChange={e => handleChangeMessageType('CmdCompletes', e.target.checked)}
										checked={currentMessageTypeToRender.CmdCompletes}>
										{_('CmdCompletes')}
									</Checkbox>
								</div>
								<div>
									<Checkbox
										id='controllerReportingMessageQueryResponsesCheckbox'
										onChange={e => handleChangeMessageType('QueryResponses', e.target.checked)}
										checked={currentMessageTypeToRender.QueryResponses}>
										{_('QueryResponses')}
									</Checkbox>
								</div>
								<div>
									<Checkbox
										id='controllerReportingMessageTitleCheckbox'
										onChange={e => handleChangeMessageType('TitleMessages', e.target.checked)}
										checked={currentMessageTypeToRender.TitleMessages}>
										{_('TitleMessages')}
									</Checkbox>
								</div>
								<div>
									<Checkbox
										id='controllerReportingMessageMiscCheckbox'
										onChange={e => handleChangeMessageType('MiscMessages', e.target.checked)}
										checked={currentMessageTypeToRender.MiscMessages}>
										{_('MiscMessages')}
									</Checkbox>
								</div>
							</div>
						</div>
					</div>
					<Divider type='vertical' className={styles.verticalDivider} />
					<div className={styles.options}>
						<span className={styles.advanceReportingSection}>{`${_('DiagnosticOptions')}:`}</span>
						<div className={styles.checkboxes}>
							<div>
								<Checkbox
									id='controllerReportingEnableCommandCheckbox'
									onChange={e => handleChangeDiagnosticOption({ EnableCommandEcho: e.target.checked })}
									checked={messagesDiagnosticOptions.EnableCommandEcho}>
									{_('EnableCommandEcho')}
								</Checkbox>
							</div>
							<div>
								<Checkbox
									id='controllerReportingEnablePrintingCheckbox'
									onChange={e => handleChangeDiagnosticOption({ EnablePrintingSNET: e.target.checked })}
									checked={messagesDiagnosticOptions.EnablePrintingSNET}>
									{_('EnablePrintingSNETMessagesOnLocalPrinter')}
								</Checkbox>
							</div>
							<div>
								<Checkbox
									id='controllerReportingEnableMCZCheckbox'
									onChange={e => handleChangeDiagnosticOption({ EnableMCZReporting: e.target.checked })}
									checked={messagesDiagnosticOptions.EnableMCZReporting}>
									{_('EnableMCZReporting')}
								</Checkbox>
							</div>
							<div>
								<Checkbox
									id='controllerReportingEnableACBCheckbox'
									onChange={e => handleChangeDiagnosticOption({ EnableACBReporting: e.target.checked })}
									checked={messagesDiagnosticOptions.EnableACBReporting}>
									{_('EnableACBReporting')}
								</Checkbox>
							</div>
						</div>
						<Divider />
						<span className={styles.advanceReportingSection}>{`${_('DisableReportingOf')}:`}</span>
						<div className={styles.checkboxes}>
							<div>
								<Checkbox
									id='controllerReportingDisableInternalCheckbox'
									onChange={e => handleChangeControllerReportingSetup({ internalEvent: e.target.checked })}
									checked={disableReportingOf.internalEvent}>
									{_('InternalEvents')}
								</Checkbox>
							</div>
							<div>
								<Checkbox
									id='controllerReportingDisableTransactionCheckbox'
									onChange={e => handleChangeControllerReportingSetup({ transactionsEvents: e.target.checked })}
									checked={disableReportingOf.transactionsEvents}>
									{_('TransactionsEvents')}
								</Checkbox>
							</div>
						</div>
					</div>
				</div>
			</div>
		</Modal>
	);
};

export { AdvanceReportingSetup };
